import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { m } from 'framer-motion';
import './Egg.scss';
import { EggTab, EggTabs, LabeledSection, SectionLabel, TabNewTag } from 'components/Egg/EggTabs';
import { useUserPoints } from 'contexts/UserPointsContext';
import { NoOmnigrowTooltip, OmnigrowSummary, OmnigrowTooltip } from './OmnigrowTooltip';
import { useEggContext } from 'contexts/EggContext';
import { routes } from '../../routing/routes';
import Spinner from '../Spinner';
import { useEggBreakerPairs } from 'contexts/EggBreakerPairContext';
import { OmnigrowPassiveTimerDate } from 'components/Omnigrow/PassiveTimer';
import { EggTabReferralTooltip } from './EggTabReferralTooltip';
import { useUser } from 'hooks/useUser';
import { getPOAPs, POAP } from 'utils/PointsAPI';
import { gtag } from 'utils/analytics';
import { POAPModal } from './POAPModal';
import { FLAGS, useFlags } from 'hooks/useFlags';
import cls from 'classnames';

function OmnigrowTimerPassiveCountdown() {
  return (
    <div className="omnigrow-passive-timer-countdown">
      <span className="next-reward">next reward in:</span>
      <OmnigrowPassiveTimerDate />
    </div>
  );
}

type EggProperties = {
  children?: React.ReactNode;
  fullscreen?: boolean;
};

export function Egg(props: EggProperties) {
  const { data: userPoints, isFetching: isPointsFetching } = useUserPoints();
  const { account } = useUser();
  const points = userPoints?.points;
  const totalPoints = userPoints?.totalPoints;
  const { data, isError, isFetching: isEggsFetching } = useEggContext();
  const eggs = data?.eggs;
  const gribbles = data?.gribbles;
  const { pairs } = useEggBreakerPairs();
  const { isFlagEnabled } = useFlags();

  const [POAPModalOpen, setPOAPModalOpen] = useState(true);
  const [unclaimedPoap, setUnclaimedPoap] = useState<POAP>();

  const tabs: EggTab[] = [
    {
      name: 'Mint',
      link: routes.mint(),
      controls: 'mint',
      id: 'mint-tab',
    },
    {
      name: 'Play',
      link: routes.play(),
      controls: 'play',
      id: 'play-tab',
    },
    {
      name: 'Eggs',
      link: routes.dashboard(),
      controls: 'eggs',
      id: 'eggs-tab',
      attachment: isError ? null : isEggsFetching ? (
        <Spinner color="white" height={12} width={12} />
      ) : eggs ? (
        <span className="egg-nav-attachment">{eggs.length}</span>
      ) : null,
      hideWhen: (isFlagEnabled) => isFlagEnabled(FLAGS.hatchening),
    },
    {
      name: 'Your Collection',
      link: routes.collection.base(),
      controls: 'collection',
      id: 'collection-tab',
      hideWhen: (isFlagEnabled) => !isFlagEnabled(FLAGS.hatchening),
    },
    {
      name: 'Missions',
      link: routes.missions(),
      controls: 'missions',
      id: 'missions-tab',
      hideWhen: (isFlagEnabled) => isFlagEnabled(FLAGS.hatchening),
    },
    {
      name: 'Story',
      link: routes.stories(),
      controls: 'stories',
      id: 'stories-tab',
      hideWhen: (isFlagEnabled) => isFlagEnabled(FLAGS.hatchening),
    },
    {
      name: 'Refer a friend',
      link: routes.referral(),
      controls: 'referral',
      id: 'referral-tab',
      tooltip: <EggTabReferralTooltip />,
    },
    {
      name: 'Hatchening',
      link: routes.hatchening(),
      controls: 'hatchening',
      id: 'hatchening-tab',
      attachment: <TabNewTag />,
      hideWhen: (isFlagEnabled) => !isFlagEnabled(FLAGS.hatchening),
    },
    {
      name: 'Contest',
      link: routes.leaderboard.base(),
      controls: 'contest',
      id: 'contest-tab',
      hideWhen: (isFlagEnabled) => isFlagEnabled(FLAGS.hatchening),
    },
  ];

  const spinner = <Spinner color="white" width={12} height={12} />;

  useEffect(() => {
    if (account.dusktech?.userId) {
      getPOAPs(account.dusktech.userId).then((POAP) => {
        const unclaimedPOAP = POAP.find((item) => !item.isClaimed);
        if (unclaimedPOAP) {
          setUnclaimedPoap(unclaimedPOAP);
          setPOAPModalOpen(true);
          gtag('event', 'poap modal');
        }
      });
    }
  }, [account.dusktech?.userId]);

  const showOmnigrowSummary = eggs !== undefined || gribbles !== undefined;

  return (
    <m.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="egg"
    >
      <Helmet>
        <title>Egg - DuskBreakers</title>
        <meta name="description" content="NEED DESCRIPTION" />
      </Helmet>

      <article id="egg-game" className={cls(props.fullscreen && 'limit-fullscreen')}>
        <div id="tab" className={cls(props.fullscreen && 'match-parent-fullscreen')}>
          <div
            className={cls(
              'container-fluid tab-container-inner',
              props.fullscreen && 'match-parent-fullscreen',
            )}
          >
            <div
              className={cls(
                'row tab-container-inner',
                props.fullscreen && 'match-parent-fullscreen',
              )}
            >
              <div className={cls('col egg-main', props.fullscreen && 'match-parent-fullscreen')}>
                <div className="mobile-omnigrow-count">
                  {showOmnigrowSummary && (
                    <LabeledSection>
                      <SectionLabel label="OmniGrow" value={isPointsFetching ? spinner : points} />
                      {totalPoints && totalPoints > 0 ? (
                        <OmnigrowSummary
                          omnigrow={points || 0}
                          eggs={eggs}
                          pairs={pairs}
                          gribbles={gribbles}
                        />
                      ) : isFlagEnabled(FLAGS.hatchening) ? (
                        <NoOmnigrowTooltip />
                      ) : (
                        <OmnigrowTooltip />
                      )}
                    </LabeledSection>
                  )}
                  {!isFlagEnabled(FLAGS.hatchening) && <OmnigrowTimerPassiveCountdown />}
                </div>
                <EggTabs
                  tabs={tabs}
                  aside={
                    <div className="desktop-omnigrow-count">
                      {showOmnigrowSummary && (
                        <LabeledSection>
                          <SectionLabel
                            label="OmniGrow"
                            value={isPointsFetching ? spinner : points}
                          />
                          {totalPoints && totalPoints > 0 ? (
                            <OmnigrowSummary
                              omnigrow={points || 0}
                              eggs={eggs}
                              pairs={pairs}
                              gribbles={gribbles}
                            />
                          ) : isFlagEnabled(FLAGS.hatchening) ? (
                            <NoOmnigrowTooltip />
                          ) : (
                            <OmnigrowTooltip />
                          )}
                        </LabeledSection>
                      )}
                      {!isFlagEnabled(FLAGS.hatchening) && <OmnigrowTimerPassiveCountdown />}
                    </div>
                  }
                />
                {props.children}
                {POAPModalOpen && unclaimedPoap && (
                  <POAPModal
                    onClose={() => {
                      setPOAPModalOpen(false);
                    }}
                    poapId={unclaimedPoap.id}
                    poapImage={unclaimedPoap.imageUrl}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </article>
    </m.main>
  );
}
